import React, { ChangeEvent, FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { STATES } from '../../../../assets/data/us_states';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/icon_close_small.svg';
import { ReactComponent as SearchActiveIcon } from '../../../../assets/icons/icon_search_active.svg';
import { ReactComponent as SearchInactiveIcon } from '../../../../assets/icons/icon_search_inactive.svg';
import { useAppSelector } from '../../../../helpers/store';
import { RootState } from '../../../../reducers/index.types';
import { SelectField } from '../../../../v2/components/SelectorField/index';
import { CityFilterInput } from './CityFilterInput';
import { contactActions } from '../../contacts.ducks';
import { MultiSelectorField } from '../../../../v2/components/MultiSelectorField';
import { change } from 'redux-form';
import { FILTER_FORM } from '../../../../constants/forms';
import { checkUserPermission, ISSUPERADMIN } from '../../../../util/userPermission';

type SelectedAudienceType = {
  name: string;
  value: string;
  type: string;
  group: string;
};
const ALL_US_BHAIO: SelectedAudienceType[] = [
  {
    name: 'All USA Bhaio',
    value: 'ALL-US-BHAIO',
    type: 'option',
    group: 'Audience'
  }
];
export const SearchCityStateFilter: FC = () => {
  const dispatch = useDispatch();
  const actions = bindActionCreators(contactActions, dispatch);
  const userAccess = useAppSelector((state: RootState) => state.userAccess);
  const { filteredCity, filteredState, searchValue, filterCitySuggestions, contactOptions } =
    useAppSelector((state: RootState) => state.contactsData);
  const initializeContactForm = useRef(false);
  const contactAudiencesOptions = useMemo(() => {
    const output = [
      {
        name: 'Audience',
        value: []
      }
    ];
    if (!contactOptions?.audiencesList?.contacts) {
      return output;
    }
    output[0].value = contactOptions?.audiencesList?.contacts;
    return output;
  }, [contactOptions?.audiencesList?.contacts]);

  const [localSearchValue, setLocalSearchValue] = useState<string>(searchValue);
  const [localCityValue, setLocalCityValue] = useState<string>(filteredCity);
  const selectedAudiences = useSelector(
    (state: RootState) => state.form[FILTER_FORM]?.values?.audiences ?? []
  );

  const largeWidthScreen = window ? window.innerWidth > 600 : false;

  const handleSearchIconClick = () => {
    if (localSearchValue?.length >= 3 || localSearchValue?.length === 0) {
      actions.updateSearchState(localSearchValue);
    }
  };
  const updateSearchValue = (event?: ChangeEvent<HTMLInputElement>) => {
    setLocalSearchValue(event?.target.value ?? '');
  };

  const handleSearchClearClick = () => {
    setLocalSearchValue('');
    actions.updateSearchState('');
  };

  const handleSearchKeyUp = (event: React.KeyboardEvent) => {
    // If the user presses enter, fire search handler
    if ((event.key === 'Enter' && localSearchValue.length >= 3) || localSearchValue.length === 0) {
      actions.updateSearchState(localSearchValue);
    }
  };
  const handleCityChange = (value: string) => {
    setLocalCityValue(value);
  };

  const handleCityFilterClear = () => {
    setLocalCityValue('');
    actions.updateFilteredCity('');
  };

  const handleSelectState = (name: string, state: string) => {
    handleCityFilterClear();
    actions.updateFilteredState(state);
  };

  const handleSelectCitySuggestion = (city: string) => {
    actions.updateFilteredCity(city);
  };

  const SearchIcon = localSearchValue?.length >= 3 ? SearchActiveIcon : SearchInactiveIcon;

  const handleSelectAudience = (name: string, value: SelectedAudienceType[]) => {
    if (Array.isArray(value)) {
      dispatch(change(FILTER_FORM, name, value));
    }
  };
  // if we don't have any audiences, that inifinite loop will happen for useEffect setting ALL-US-BHAIO
  const isSuperAdmin = useMemo(() => checkUserPermission(userAccess, ISSUPERADMIN), [userAccess]);
  const listAvailable = useMemo(() => {
    return contactOptions?.audiencesList?.contacts?.length > 0;
  }, [contactOptions?.audiencesList?.contacts]);

  useEffect(() => {
    // if Super Admin than select ALL-US-BHAIO
    // do it only once when contactOptions is available
    if (isSuperAdmin && listAvailable && dispatch && change && !initializeContactForm.current) {
      if (!initializeContactForm.current) {
        initializeContactForm.current = true;
        dispatch(change(FILTER_FORM, 'audiences', ALL_US_BHAIO));
      }
    }
  }, [isSuperAdmin, listAvailable, dispatch, change]);

  return (
    <Fragment>
      <div className="contact-search-icon-container">
        <SearchIcon className="icon-search" onClick={handleSearchIconClick} />
        <input
          id="contact-search-field"
          className={[
            'contact-filter-search',
            localSearchValue && localSearchValue.length >= 3 ? 'active' : null
          ].join(' ')}
          type="text"
          placeholder="Search contacts"
          value={localSearchValue}
          onChange={updateSearchValue}
          onKeyUp={handleSearchKeyUp}
        />
        {searchValue ? <CloseIcon className="icon-close" onClick={handleSearchClearClick} /> : null}
      </div>
      {largeWidthScreen ||
      (searchValue && searchValue.length >= 0) ||
      localCityValue ||
      filteredState ? (
        <Fragment>
          <SelectField
            containerClassName="contact-filter-state-input-container"
            className="contact-filter-state-input"
            placeholder="State"
            options={STATES}
            onChange={handleSelectState}
            inputValue={filteredState}
          />
          <div className="contact-filter-city-input-container">
            <CityFilterInput
              className="contact-filter-city-input"
              value={localCityValue}
              isDisabled={filteredState}
              filterCitySuggestions={filterCitySuggestions}
              onChange={handleCityChange}
              onClear={handleCityFilterClear}
              onSelectSuggestion={handleSelectCitySuggestion}
            />
          </div>
        </Fragment>
      ) : null}
      <div className="contact-filter-city-audience-container">
        <MultiSelectorField
          className="contact-filter-audience-input"
          input={{ value: selectedAudiences }}
          placeholder="Select Audience"
          onChange={handleSelectAudience}
          name="audiences"
          buttonLabel="Audience(s)"
          options={contactAudiencesOptions}
        />
      </div>
    </Fragment>
  );
};
